"use client";

import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { MapPin, Truck, CheckCircle2, Clock, AlertCircle, Navigation2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface CourierLocation {
  lat: number;
  lng: number;
  updatedAt: string | null;
}

interface DeliveryTrackingPanelProps {
  order: any;
  activeAssignment: any;
  courierLiveLocation: CourierLocation | null;
  isOrderTrackable: boolean;
  loading?: boolean;
}

// Timeline stages del envío
const DELIVERY_STAGES = [
  {
    key: 'assigned',
    label: 'Repartidor asignado',
    icon: CheckCircle2,
    description: 'Tu repartidor está en camino',
  },
  {
    key: 'picked_up',
    label: 'Recogido en local',
    icon: MapPin,
    description: 'El paquete fue retirado',
  },
  {
    key: 'in_delivery',
    label: 'En entrega',
    icon: Truck,
    description: 'Llegando a tu puerta',
  },
  {
    key: 'delivered',
    label: 'Entregado',
    icon: CheckCircle2,
    description: 'Entrega completada',
  },
];

function getStageFromNotes(notes: string | null | undefined): string | null {
  if (!notes) return null;

  try {
    const parsed = JSON.parse(notes);
    return parsed?.stage || null;
  } catch {
    return null;
  }
}

function getCurrentStageIndex(dispatchStatus: string, notes: string | null): number {
  const stage = getStageFromNotes(notes);

  if (dispatchStatus === 'delivered') return 3;
  if (stage === 'picked_up' || stage === 'arrived_local') return 1;
  if (dispatchStatus === 'in_delivery' || stage === 'in_delivery') return 2;
  if (dispatchStatus === 'assigned') return 0;

  return 0;
}

function getCurrentStageName(dispatchStatus: string, notes: string | null): string {
  const stage = getStageFromNotes(notes);

  if (dispatchStatus === 'delivered') return 'delivered';
  if (stage === 'picked_up' || stage === 'arrived_local') return 'picked_up';
  if (dispatchStatus === 'in_delivery' || stage === 'in_delivery') return 'in_delivery';
  if (dispatchStatus === 'assigned') return 'assigned';

  return 'assigned';
}

export function DeliveryTrackingPanel({
  order,
  activeAssignment,
  courierLiveLocation,
  isOrderTrackable,
  loading = false,
}: DeliveryTrackingPanelProps) {
  const [expandedMap, setExpandedMap] = useState(false);

  if (!isOrderTrackable || !order) {
    return null;
  }

  const currentStageIndex = getCurrentStageIndex(order.dispatch_status, activeAssignment?.notes);
  const currentStageName = getCurrentStageName(order.dispatch_status, activeAssignment?.notes);
  const courierStage = getStageFromNotes(activeAssignment?.notes);

  return (
    <div className="mb-6 space-y-4 rounded-lg border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/2 p-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Truck className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-foreground">Seguimiento de entrega</h3>
        </div>
        {courierLiveLocation?.updatedAt && (
          <span className="text-xs text-muted-foreground">
            Actualizado {format(new Date(courierLiveLocation.updatedAt), 'HH:mm', { locale: es })}
          </span>
        )}
      </div>

      {/* Timeline */}
      <div className="space-y-3">
        {DELIVERY_STAGES.map((stageConfig, index) => {
          const isComplete = index <= currentStageIndex;
          const isCurrent = index === currentStageIndex;
          const IconComponent = stageConfig.icon;

          return (
            <div key={stageConfig.key} className="flex gap-3">
              {/* Timeline line and dot */}
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    'h-8 w-8 rounded-full border-2 flex items-center justify-center transition-all',
                    isComplete
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-muted-foreground/30 bg-muted text-muted-foreground'
                  )}
                >
                  <IconComponent className="h-4 w-4" />
                </div>

                {/* Connecting line to next stage */}
                {index < DELIVERY_STAGES.length - 1 && (
                  <div
                    className={cn(
                      'w-0.5 h-8 my-1 transition-colors',
                      isComplete ? 'bg-primary' : 'bg-muted-foreground/20'
                    )}
                  />
                )}
              </div>

              {/* Stage info */}
              <div className="flex-1 pt-1.5">
                <div
                  className={cn(
                    'font-medium transition-colors',
                    isCurrent
                      ? 'text-primary'
                      : isComplete
                        ? 'text-foreground'
                        : 'text-muted-foreground'
                  )}
                >
                  {stageConfig.label}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {stageConfig.description}
                </div>

                {/* Current stage badge */}
                {isCurrent && (
                  <div className="mt-2">
                    <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
                      En progreso
                    </Badge>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Courier info */}
      {activeAssignment && (
        <div className="rounded-lg bg-background/50 p-3 border border-border/50">
          <div className="text-xs text-muted-foreground mb-1">Repartidor asignado</div>
          <div className="font-medium text-sm">{activeAssignment.courier_id}</div>
          {courierStage && (
            <div className="text-xs text-muted-foreground mt-1">
              Estado: <span className="text-foreground font-medium capitalize">{courierStage.replace('_', ' ')}</span>
            </div>
          )}
        </div>
      )}

      {/* Live location map */}
      {courierLiveLocation && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Navigation2 className="h-4 w-4 text-emerald-500" />
            <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
              Ubicación en tiempo real
            </span>
          </div>

          <div
            className={cn(
              'relative overflow-hidden rounded-lg border border-border bg-muted transition-all duration-300',
              expandedMap ? 'h-96' : 'h-44 cursor-pointer hover:border-primary'
            )}
            onClick={() => !expandedMap && setExpandedMap(true)}
          >
            <iframe
              title="Mapa en tiempo real del repartidor"
              src={`https://maps.google.com/maps?q=${courierLiveLocation.lat},${courierLiveLocation.lng}&z=16&output=embed`}
              className="h-full w-full"
              loading="lazy"
              frameBorder="0"
            />

            {/* Click to expand hint */}
            {!expandedMap && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/5 transition-colors">
                <div className="text-center text-xs text-white drop-shadow-lg font-medium">
                  Haz clic para ampliar
                </div>
              </div>
            )}

            {/* Close button when expanded */}
            {expandedMap && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedMap(false);
                }}
                className="absolute top-2 right-2 z-10 rounded-lg bg-background/90 p-1.5 hover:bg-background transition-colors"
                aria-label="Contraer mapa"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>

          <div className="text-xs text-muted-foreground">
            {courierLiveLocation.updatedAt
              ? `Última actualización: ${format(new Date(courierLiveLocation.updatedAt), 'dd/MM HH:mm:ss', {
                  locale: es,
                })}`
              : 'Actualizando ubicación...'}
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="space-y-2">
          <Skeleton className="h-32 w-full rounded-lg" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      )}

      {/* No active courier yet */}
      {!activeAssignment && isOrderTrackable && (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/30 p-3 flex gap-2">
          <Clock className="h-4 w-4 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-800 dark:text-amber-200">
            Tu repartidor será asignado pronto. Recibirás una notificación cuando comience el viaje.
          </div>
        </div>
      )}
    </div>
  );
}
